// arrhythmiaVocabulary.ts

/**
 * Turn the arrhythmia the machine recorded into the words THIS clinic uses.
 *
 * The backend reads the finding out of the CardioSoft export's narrative and
 * returns it as a canonical slug (`v2/helper/stressArrhythmia.js`). It never
 * returns wording, because the report's arrhythmia sentence is a phrase slot
 * backed by each clinic's own dropdown list, and those lists differ — one writes
 * "atrial ectopics", another may write "atrial premature beats", a third
 * separates ectopics into "occasional" and "frequent".
 *
 * So this resolves a finding against the clinic's OWN options and returns null
 * when the list cannot express it. Exactly the rule `wordingVocabulary.ts`
 * follows for the measurement-driven sentences: a sentence in a signed report
 * reads in the clinic's voice, or it is left alone for a human to write.
 */

export type ArrhythmiaFinding =
  | 'ventricular_tachycardia'
  | 'non_sustained_vt'
  | 'ventricular_couplets'
  | 'ventricular_ectopics'
  | 'supraventricular_tachycardia'
  | 'atrial_fibrillation'
  | 'atrial_flutter'
  | 'atrial_ectopics'
  | 'heart_block';

/** What the backend detector returns. `null` means the export said nothing about rhythm. */
export type DetectedArrhythmia = {
  findings: ArrhythmiaFinding[];
  negated: boolean;
  frequency: 'occasional' | 'frequent' | null;
  source: string;
};

/** The slot this fills. The template's "The following arrhythmias were noted: ..." span. */
export const ARRHYTHMIA_SLOT = 'stress-arrhythmia';

/**
 * Candidate wordings per finding, most specific first. Matched AGAINST a clinic's
 * list — never inserted directly.
 *
 * The synonyms are the accepted clinical names for one rhythm: atrial premature
 * beats, atrial premature complexes, PACs and atrial ectopics are the same
 * finding, and a clinic may have listed any of them.
 */
const FINDING_SYNONYMS: Record<ArrhythmiaFinding, string[]> = {
  ventricular_tachycardia: ['sustained ventricular tachycardia', 'ventricular tachycardia'],
  non_sustained_vt: [
    'non-sustained ventricular tachycardia',
    'non sustained ventricular tachycardia',
    'nsvt',
  ],
  ventricular_couplets: ['ventricular couplets', 'couplets'],
  ventricular_ectopics: [
    'ventricular ectopics',
    'ventricular ectopy',
    'ventricular premature beats',
    'premature ventricular complexes',
    'pvcs',
  ],
  supraventricular_tachycardia: ['supraventricular tachycardia', 'svt'],
  atrial_fibrillation: ['atrial fibrillation'],
  atrial_flutter: ['atrial flutter'],
  atrial_ectopics: [
    'atrial ectopics',
    'atrial ectopy',
    'atrial premature beats',
    'atrial premature complexes',
    'premature atrial contractions',
    'pacs',
  ],
  heart_block: ['heart block', 'av block'],
};

/** Wording for "the study was clean". Matched the same way as a finding. */
const NEGATED_SYNONYMS = ['no arrhythmias', 'no significant arrhythmias', 'none', 'nil'];

const normalize = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:]+$/, '');

/**
 * First option whose text matches one of `candidates`.
 *
 * Exact, then "starts with", then "contains" — so "frequent ventricular
 * ectopics" is preferred over a bare "ectopics" entry when the candidate is the
 * longer phrase, and a list written as a sentence fragment still resolves. Loose
 * containment is only allowed for candidates long enough to be unambiguous;
 * "pacs" or "svt" matching inside an unrelated sentence would be a wrong finding
 * in a clinical report, which is worse than filling nothing.
 */
function matchOption(candidates: string[], options: string[]): string | null {
  for (const candidate of candidates) {
    const exact = options.find(option => normalize(option) === candidate);
    if (exact) {
      return exact;
    }
  }
  // Both loose tiers are barred to the short abbreviations. "pacs" as a prefix
  // matches "pacsonly placeholder", and "svt" as a substring matches plenty of
  // ordinary words — either would put a rhythm in a report that the study never
  // showed. An abbreviation therefore only ever matches an option exactly.
  const loose = candidates.filter(candidate => candidate.length >= 8);
  for (const candidate of loose) {
    const startsWith = options.find(option => normalize(option).startsWith(candidate));
    if (startsWith) {
      return startsWith;
    }
  }
  for (const candidate of loose) {
    const contains = options.find(option => normalize(option).includes(candidate));
    if (contains) {
      return contains;
    }
  }
  return null;
}

/**
 * The clinic's own wording for a detected arrhythmia, or null when its list
 * cannot express it.
 *
 * Only the WORST finding is written. The slot is one phrase, and every clinic's
 * list is single findings rather than combinations, so a study with couplets and
 * ectopics reports the couplets — the detector already returns findings ranked
 * worst-first for this reason. The rest stay in the source narrative for the
 * clinician, and are named in the note the caller raises.
 *
 * When the clinic separates a finding by frequency ("occasional ventricular
 * ectopics" vs "frequent ventricular ectopics") and the export stated one, the
 * qualified option is preferred. Without a stated frequency the plain option
 * wins — guessing "frequent" from silence would change the finding's meaning.
 */
export function resolveArrhythmiaPhrase(
  detected: DetectedArrhythmia | null | undefined,
  options: string[] | undefined
): string | null {
  if (!detected || !Array.isArray(options) || options.length === 0) {
    return null;
  }

  if (detected.negated) {
    return matchOption(NEGATED_SYNONYMS, options);
  }

  const finding = detected.findings?.[0];
  if (!finding || !FINDING_SYNONYMS[finding]) {
    return null;
  }

  const synonyms = FINDING_SYNONYMS[finding];

  if (detected.frequency) {
    const qualified = options.find(option => {
      const text = normalize(option);
      return (
        text.startsWith(detected.frequency as string) &&
        synonyms.some(synonym => text.includes(synonym))
      );
    });
    if (qualified) {
      return qualified;
    }
  }

  return matchOption(synonyms, options);
}
